import React, { useEffect, useContext } from 'react';
import { useLocation, useRouteMatch, useHistory } from 'react-router-dom';
import { Form } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
// @ts-ignore
import setFieldData from 'final-form-set-field-data';
// @ts-ignore
import { GlobalAppStyles, ThemeContext } from '@qover/styles';
import { MailCampaignService } from '@qover/services';
import Config from '@qover/utils/src/Config';
import {
  useDetailsPolyfill,
  useTerms,
  useDraft,
  useDraftIdRedirect,
} from '@qover/hooks';
import { EmailModalPopup } from '@qover/organisms';
import { TripType } from '@qover-private/protos-web/qover/dojo/travel/v1/terms';
import { UpdateDraftLanguageAndCountry } from '@qover/utils/src/UpdateDraftLanguageAndCountry';
import { Contract as DraftProtos } from '@qover-private/protos-web/qover/dojo/travel/v1/contract';
import Layout from './pages/layout/Layout';
import { errorHandler, termsErrorHandler } from './utils/trErrorFormatter';
import { pushPaymentCheckoutDesktop } from './domain/customDataLayer';
import { productService } from './services/productService';
import { priceInfoService } from './services/priceInfoServices';
import { useSteps } from './hooks/useSteps';
import PriceInfo from './domain/PriceInfo';
import { ChatStyle } from './travel.styles';
import { Routes } from './Routes';
import * as DraftFactory from './domain/factories/DraftFactory';
import { Draft } from './domain/Draft';
import type { DraftProps } from './domain/Draft';
import { Risk } from './domain/Risk';
import { trDecorators } from './utils/trDecorators';
import { UpdatePersonCount } from './components/UpdatePersonCount';
import { WarningEngine } from './utils/trFormSpy';

interface TravelProps {
  chat: any;
  draft?: DraftProtos;
  head: React.FC;
}

function Travel(props: TravelProps) {
  useDetailsPolyfill();
  const history = useHistory();
  const match = useRouteMatch();
  const location = useLocation();
  const search = new URLSearchParams(location.search);

  const theme = useContext<any>(ThemeContext);

  const wizard = useDraft<Draft, DraftProtos, PriceInfo>(
    DraftFactory.createFromApiResponse(props.draft),
    productService,
    new PriceInfo(),
    priceInfoService,
  );
  const { draft, priceInfo, ...TrWizard } = wizard;

  const { currentStep, completionPercentage, steps } = useSteps();
  const { path, nextPath } = currentStep ?? {};
  useDraftIdRedirect(currentStep, draft?.draftId);
  const terms = useTerms(currentStep?.fetchTerms);

  const remarketing = theme?.featureToggles?.track?.mailchimp?.remarketing;
  const isExpat = path === 'quote-expat';

  useEffect(() => {
    if (isExpat || (draft.terms?.tripType && currentStep?.fetchPrices)) {
      TrWizard.getPriceInfo();
    }
  }, [Risk.computePersonsCount(draft.risk), draft.terms?.tripType]);

  const handleStepChange = async (
    form: Draft,
    redirectTo: string,
    from?: string,
  ) => {
    try {
      currentStep?.ecommerce?.(form, priceInfo);
    } catch (e) {
      console.error(e);
    }

    const response = await TrWizard.upsertDraft(form);

    search.set('id', response.draftId || '');

    if (from) {
      search.set('from', from);
    }

    if (remarketing && path === 'finalization') {
      MailCampaignService.updateStep(response, 'summary');
    }
    history.push(`${match.path}/${redirectTo}?${search.toString()}`);
  };

  const onSubmit = (form: DraftProps) => {
    const draftForm = new Draft(form);

    if (nextPath) {
      const from = path?.startsWith('quote') ? path : undefined;
      return handleStepChange(draftForm, nextPath, from).catch(errorHandler);
    }

    if (path === 'trip-duration') {
      let redirect = 'quote';

      if (form?.terms?.tripType === TripType.TRIP_TYPE_SINGLE_TRIP) {
        redirect = 'short-trip-location';
      } else if (Draft.isMultiTripPremium(form)) {
        redirect = 'quote-multi-trip';
      }

      return handleStepChange(draftForm, redirect).catch(errorHandler);
    }

    // Only for summary
    return TrWizard.prepareForPayment(draftForm, terms)
      .then(token => {
        pushPaymentCheckoutDesktop(draft);
        search.set('token', token);
        history.push(`${match.path}/payment?${search.toString()}`);
      })
      .catch(termsErrorHandler)
      .catch(errorHandler);
  };

  const linkTo = (to: string) => `${match.path}/${to}?${search.toString()}`;

  const onEmailModalSubmit = (step: string) => (form: { email: string }) =>
    TrWizard.updateEmail(step, form.email);

  const showEmailModal =
    !draft?.policyholder?.email &&
    !!path &&
    !!['quote', 'options', 'insurers', 'finalization'].includes(path);
  const { head: Head } = props;

  return (
    <>
      <Head />
      <GlobalAppStyles />
      <ChatStyle />
      <EmailModalPopup
        bindToWindowObject={Config.env !== 'production'}
        draftId={draft?.draftId}
        showEmailModal={showEmailModal}
        onSubmit={onEmailModalSubmit('leave')}
      />
      <Form
        // @ts-ignore
        decorators={trDecorators()}
        // @ts-ignore
        mutators={{ ...arrayMutators, setFieldData }}
        onSubmit={onSubmit}
        initialValues={draft}
        validate={currentStep?.validate}
      >
        {({ handleSubmit, values, form }) => (
          <Layout
            chat={props.chat}
            completionPercentage={completionPercentage}
            currentStep={currentStep}
            linkTo={linkTo}
            steps={steps}
            flowStartUrl={`${match.path}/trip-duration`}
            wizard={TrWizard}
          >
            <UpdateDraftLanguageAndCountry />
            {path !== 'payment' && (
              <UpdatePersonCount
                updateDraft={async() => {
                  await TrWizard.updateDraft(new Draft(values))
                }}
                draftId={values.draftId}
              />
            )}
            <Routes
              currentStep={currentStep}
              draft={draft}
              form={values}
              handleSubmit={handleSubmit}
              linkTo={linkTo}
              match={match}
              prices={priceInfo!}
              risk={values?.risk}
              terms={terms}
              wizard={TrWizard}
            />
            <WarningEngine mutators={form.mutators} />
          </Layout>
        )}
      </Form>
    </>
  );
}

Travel.displayName = 'Travel';

export default Travel;
